import { useMemo } from 'react'

import {
  Link, ProgressBar, StatusBadge, Table, Text,
} from '@domain-group/suit-theme'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import { TaskListModel } from '@models/taskList'

import type { EntitiesState } from '@redux/entities'

import useSelector from '@hooks/useSelector'
import { calculateCompletionDecimal, generateUrls, useRelations } from '@hooks/useTaskList'

import DateBadge from '@components/DateBadge'

const watchEntityKeys = ['statuses', 'taskListItems']

const OwnerColumn = (props: { taskList: TaskListModel }) => {
  const { taskList } = props

  const { brand } = useRelations(taskList)

  return (
    <Text width={180}>
      {/*
       // @ts-expect-error TS2339 */}
      {brand?.title}
    </Text>
  )
}

const buildColumns = (entities: EntitiesState) => {
  const { taskLists } = entities

  return [
    {
      title: 'Progress',
      cellProps: {
        verticalAlign: 'middle',
        width: 180,
      },
      dataKey: 'progress_status',
      render: (progressStatus: string, taskList: TaskListModel) => {
        const notStarted = progressStatus === 'not_started'
        const decimalValue = notStarted ? 0 : calculateCompletionDecimal(taskList)
        const labelColor = notStarted ? '#c5c5c5' : 'white'
        const progressBarColor = notStarted ? '#f8f8f8' : 'blue'

        return (
          <ProgressBar
            animateProgress
            decimal={decimalValue}
            height={20}
            labelColor={labelColor}
            progressBarColor={progressBarColor}
            width={180}
          />
        )
      },
    },
    {
      title: 'Project',
      dataKey: 'title',
      render: (title: string, taskList: TaskListModel) => {
        const { editTaskListUrl } = generateUrls(taskList)

        return (
          <Link
            href={editTaskListUrl}
            key={title}
            textProps={{ color: 'bodyFontColor', display: 'inline-block', width: '100%' }}
          >
            {title || 'Untitled'}
          </Link>
        )
      },
    },
    {
      title: 'Brand',
      dataKey: 'owner_id',
      // @ts-expect-error TS7006
      render: (_, taskList: TaskListModel) => <OwnerColumn taskList={taskList} />,
    },
    {
      title: 'Due Date',
      cellProps: {
        key: 'due_date',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'id',
      render: (id: number) => {
        const taskList = taskLists[id] || {}
        // @ts-expect-error TS2339
        const dueDate = taskList?.dates?.due?.date

        if (!dueDate) return (<Text fontStyle="italic">Not Set</Text>)

        return (
          <DateBadge date={dueDate} />
        )
      },
    },
    {
      title: 'Remaining',
      cellProps: {
        textAlign: 'center',
      },
      dataKey: 'task_counts',
      render: (taskCounts: { incomplete: number }) => (
        <StatusBadge
          boxProps={{ borderRadius: 10, borderWidth: 2 }}
          color="#02b7e2"
          ghost
          text={`${taskCounts.incomplete}`}
          textProps={{ fontWeight: 'bold' }}
        />
      ),
    },
  ]
}

interface TaskListTableProps {
  taskLists: TaskListModel[],
}

const TaskListTable = (props: TaskListTableProps) => {
  const { taskLists } = props

  const entities = useSelector(reduxState => reduxState.entities)

  const {
    updatedEntities: {
      statuses: statusesUpdatedAt,
      taskListItems: taskListItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const memoColumns = useMemo(
    () => buildColumns(entities),
    [statusesUpdatedAt, taskLists.length, taskListItemsUpdatedAt],
  )

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 600, overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={memoColumns}
      data={taskLists}
      stickyHeader
    />
  )
}

export default TaskListTable
